import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { TableSearch } from '../../../../../components/form/Fields';
import CustomTable from '../../../../../components/table/CustomTable';
import CommonButton from '../../../../../components/commonButton';
import { renderPropertyImageCell, usePropertyImagePreview } from '../../../../../utils/otherHelopers';
import { routes } from '../../../../../routes/pageRoutes';
import Axios, { authHeaders } from '../../../../../utils/axios';
import { formatMomentDate } from '../../../../../utils/dateHelper';
import { useDispatch } from 'react-redux';
import { useToast } from '../../../../../context/TostContext';
import DeletePopup from '../../../../../components/delete-popup';
import {
    changeStatus,
    changePublicStatus,
    archiveProperty,
    unarchiveProperty,
    deleteAllProperty,
} from '../../../../../redux/slice/allPropertiesSlice';
import StatusChip from '../../../../../components/statusChip/StatusChip';


const StatusChipForProperty = ({ status }) => {
    const bg =
        status === 'Close' ? 'warning' :
            status === 'Live' ? 'success' :
                status === 'Open' ? 'blue' :
                    status === 'Expired' ? 'danger' :
                        '';
    return <StatusChip bg={bg} pill title={status} className="text-capitalize" />;
};

const buildLandColumns = (onPreview) => [
    {
        id: 'image',
        header: 'IMAGES',
        accessor: 'photos',
        cell: (_, row) => renderPropertyImageCell(row, onPreview),
    },
    {
        id: 'client',
        header: 'CLIENT',
        accessor: 'client',
        cell: (value) => (
            <div className="d-flex flex-column text-start">
                <span className="fw-semibold text-dark text-nowrap">{value?.name}</span>
                <span className="small text-muted" style={{ fontSize: '11px' }}>{value?.mobile_number || "—"}</span>
                <span className="small text-muted" style={{ fontSize: '11px' }}>{value?.email || "—"}</span>
            </div>
        )
    },
    {
        id: 'clientType',
        header: 'CLIENT TYPE',
        accessor: 'client_type',
    },
    {
        id: 'transactionType',
        header: 'TRANSACTION TYPE',
        accessor: 'transaction_type',
    },
    {
        id: 'landType',
        header: 'LAND TYPE',
        accessor: 'land_type',
        cell: (v) => <span className="fw-semibold text-dark">{v || "—"}</span>
    },
    {
        id: 'tenure',
        header: 'TENURE',
        accessor: 'tenure',
    },
    {
        id: 'location',
        header: 'LOCATION',
        cell: (_, row) => {
            const text = [row.village?.name, row.taluka?.name, row.district?.name, row.state?.name]
                .filter(Boolean)
                .join(', ');
            return <span className="small text-muted">{text || '—'}</span>;
        },
    },
    {
        id: 'landArea',
        header: 'LAND AREA',
        accessor: "land_area",

    },
    {
        id: 'landAreaUnit',
        header: 'LAND AREA UNIT',
        accessor: 'land_area_unit'
    },
    {
        id: 'pricePerUnit',
        header: 'PRICE PER UNIT',
        accessor: "price_per_unit",
    },
    {
        id: 'priceUnit',
        header: 'PRICE UNIT',
        accessor: "price_unit"
    },
    {
        id: 'totalPrice',
        header: 'TOTAL PRICE',
        accessor: "total_price"
    },
    {
        id: 'status',
        header: 'STATUS',
        accessor: 'status',
        cell: (v) => v ? <StatusChipForProperty status={v} /> : '—',
    },
    {
        id: 'isPublic',
        header: 'IS PUBLIC',
        accessor: "is_public",
        cell: (v) => (
            <StatusChip
                bg={v ? 'success' : 'danger'}
                pill
                title={v ? 'Yes' : 'No'}
            />
        ),
    },
    {
        id: 'isArchived',
        header: 'IS ARCHIVED',
        accessor: "is_archived",
        cell: (v) => (
            <StatusChip
                bg={v ? "danger" : "success"}
                pill
                title={v ? "Yes" : "No"}
            />
        )
    },

    {
        id: 'createdAt',
        header: 'CREATED AT',
        accessor: "created_at",
        cell: (v) => formatMomentDate(v, "DD-MM-YYYY hh:mm A")
    },
    {
        id: 'updatedAt',
        header: 'UPDATED AT',
        accessor: "updated_at",
        cell: (v) => formatMomentDate(v, "DD-MM-YYYY hh:mm A")
    }
];

const buildPropertyColumns = (onPreview) => [
    {
        id: 'image',
        header: 'IMAGES',
        accessor: 'files',
        cell: (_, row) => renderPropertyImageCell(row, onPreview),
    },
    {
        id: 'client',
        header: 'CLIENT',
        accessor: 'client',
        cell: (v) => (
            <div className="d-flex flex-column text-start">
                <span className="fw-semibold text-dark text-nowrap">{v?.name || "—"}</span>
                <span className="small text-muted" style={{ fontSize: '11px' }}>{v?.mobile_number || "—"}</span>
                <span className="small text-muted" style={{ fontSize: '11px' }}>{v?.email || "—"}</span>
            </div>
        )
    },
    {
        id: 'clientType',
        header: 'CLIENT TYPE',
        accessor: 'client_type'
    },
    {
        id: 'transactionType',
        header: 'TRANSACTION TYPE',
        accessor: 'transaction_type'
    },
    {
        id: 'propertyType',
        header: 'PROPERTY TYPE',
        accessor: 'property_type',
        cell: (v) => <span className="fw-semibold text-dark">{v || "—"}</span>
    },
    {
        id: 'location',
        header: 'LOCATION',
        cell: (_, row) => {
            const text = [row.locality?.name, row.city?.name, row.state?.name]
                .filter(Boolean)
                .join(', ');
            return <span className="small text-muted">{text || '—'}</span>;
        },
    },
    {
        id: 'sBuiltupArea',
        header: 'S. BUILTUP AREA',
        accessor: "s_builtup_area"
    },
    {
        id: 'sBuiltupAreaUnit',
        header: 'S. BUILTUP AREA UNIT',
        accessor: "s_builtup_area_unit"
    },
    {
        id: 'plotArea',
        header: 'PLOT AREA',
        accessor: "plot_area"
    },
    {
        id: 'plotAreaUnit',
        header: 'PLOT AREA UNIT',
        accessor: "plot_area_unit"
    },
    {
        id: 'possession',
        header: 'POSSESSION',
        accessor: "possession"
    },
    {
        id: 'furnishingStatus',
        header: 'FURNISHING STATUS',
        accessor: "furnishing_status"
    },
    {
        id: 'pricePerUnit',
        header: 'PRICE PER UNIT',
        accessor: 'price_per_unit'
    },
    {
        id: 'priceUnit',
        header: 'PRICE UNIT',
        accessor: 'price_unit'
    },
    {
        id: 'totalPrice',
        header: 'TOTAL PRICE',
        accessor: 'total_price'
    },
    {
        id: 'status',
        header: 'STATUS', accessor: 'status',
        cell: (v) => v ? <StatusChipForProperty status={v} /> : '—',
    },
    {
        id: 'isPublic',
        header: 'IS PUBLIC',
        accessor: 'is_public',
        cell: (v) => (
            <StatusChip
                bg={v ? 'success' : 'danger'}
                pill
                title={v ? 'Yes' : 'No'}
            />
        ),
    },
    {
        id: 'isArchived',
        header: 'IS ARCHIVED',
        accessor: 'is_archived',
        cell: (v) => (
            <StatusChip
                bg={v ? 'danger' : 'success'}
                pill
                title={v ? 'Yes' : 'No'}
            />
        ),
    },
    {
        id: 'createdAt',
        header: 'CREATED AT',
        cell: (_, row) => formatMomentDate(row.created_at, "DD-MM-YYYY hh:mm A")
    },
    {
        id: 'updatedAt',
        header: 'UPDATED AT',
        cell: (_, row) => formatMomentDate(row.updated_at, "DD-MM-YYYY hh:mm A")
    }
];

const Properties = ({ userId, userDetail, isLand }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const toast = useToast();

    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(false);
    const [dataList, setDataList] = useState([]);
    const [total, setTotal] = useState(0);
    const { setPreviewMedia, renderPreviewModal } = usePropertyImagePreview();

    const fetchData = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const response = await Axios.get('/broker/properties', {
                params: {
                    page: currentPage,
                    limit: pageSize,
                    search: searchTerm,
                    user_id: userId,
                    is_land: isLand ? 'true' : 'false',
                },
                ...authHeaders(),
            });
            const result = response.data;
            setDataList(result?.data || result?.rows || result || []);
            setTotal(result?.meta?.total || result?.count || (result?.data || result || []).length);
        } catch (error) {
            console.error(`Failed to fetch Broker CRM ${isLand ? 'lands' : 'properties'}:`, error);
        } finally {
            setLoading(false);
        }
    }, [userId, isLand, currentPage, pageSize, searchTerm]);

    // Reset on isLand switch
    useEffect(() => {
        setSearchTerm('');
        setCurrentPage(1);
        setDataList([]);
        setTotal(0);
    }, [isLand]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const totalPages = Math.ceil(total / pageSize);
    const activePage = Math.min(currentPage, Math.max(1, totalPages));

    const handleToggleStatus = async (row) => {
        try {
            const newStatus = row.status === 'Open' ? 'Close' : 'Open';
            const res = await dispatch(changeStatus({ id: row.id, status: newStatus }));
            if (res.error) {
                toast.error(res.payload?.message || 'Failed to change status');
            } else {
                toast.success(`Property status updated to ${newStatus}`);
                setDataList((prev) =>
                    prev.map((item) =>
                        item.id === row.id ? { ...item, status: newStatus, updated_at: new Date().toISOString() } : item
                    )
                );
            }
        } catch {
            toast.error('Failed to change status');
        }
    };

    const handleTogglePublic = async (row) => {
        try {
            const newPublic = !row.is_public;
            const res = await dispatch(changePublicStatus({ id: row.id, is_public: newPublic }));
            if (res.error) {
                toast.error(res.payload?.message || 'Failed to change visibility');
            } else {
                toast.success(`Property is now ${newPublic ? 'Public' : 'Private'}`);
                setDataList((prev) =>
                    prev.map((item) =>
                        item.id === row.id ? { ...item, is_public: newPublic, updated_at: new Date().toISOString() } : item
                    )
                );
            }
        } catch {
            toast.error('Failed to change visibility');
        }
    };

    const handleToggleArchive = async (row) => {
        try {
            const newArchived = !row.is_archived;
            const action = newArchived ? archiveProperty({ id: row.id }) : unarchiveProperty({ id: row.id });
            const res = await dispatch(action);
            if (res.error) {
                toast.error(res.payload?.message || 'Failed to change archive status');
            } else {
                toast.success(newArchived ? 'Property archived successfully' : 'Property unarchived successfully');
                setDataList((prev) =>
                    prev.map((item) =>
                        item.id === row.id ? { ...item, is_archived: newArchived, updated_at: new Date().toISOString() } : item
                    )
                );
            }
        } catch {
            toast.error('Failed to change archive status');
        }
    };

    const handleDeleteConfirm = async () => {
        if (!itemToDelete) return;
        setDeleting(true);
        try {
            const res = await dispatch(deleteAllProperty({ id: itemToDelete }));
            if (res.error) {
                toast.error(res.payload?.message || 'Failed to delete property');
            } else {
                toast.success('Property deleted successfully');
                setShowDeletePopup(false);
                setItemToDelete(null);
                fetchData();
            }
        } finally {
            setDeleting(false);
        }
    };

    const actionList = [
        {
            title: 'View',
            handler: (row) => {
                navigate(`/broker-crm/properties/property-details/${row.id}`, {
                    state: { id: row.id, currentPage: 1 },
                });
            },
        },
        {
            title: 'Edit',
            handler: (row) => {
                navigate(`/broker-crm/properties/edit/${row.id}`, {
                    state: {
                        isEdit: true,
                        item: row,
                        fromUserDetail: true,
                        isLand,
                    },
                });
            },
        },
        {
            title: (row) => (row.status === 'Open' ? 'Close' : 'Open'),
            handler: handleToggleStatus,
        },
        {
            title: (row) => (row.is_public ? 'Make Private' : 'Make Public'),
            handler: handleTogglePublic,
        },
        {
            title: (row) => (row.is_archived ? 'Unarchive' : 'Archive'),
            handler: handleToggleArchive,
        },
        {
            title: 'Delete',
            handler: (row) => {
                setItemToDelete(row.id);
                setShowDeletePopup(true);
            },
            isDanger: true,
        },
    ];

    const columns = isLand
        ? buildLandColumns(setPreviewMedia)
        : buildPropertyColumns(setPreviewMedia);

    return (
        <div>
            {/* Toolbar */}
            <div className="d-flex align-items-center justify-content-between gap-4 mb-4">
                <TableSearch
                    value={searchTerm}
                    placeholder="Search"
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }}
                    onClear={() => {
                        setSearchTerm('');
                        setCurrentPage(1);
                    }}
                />
                <CommonButton
                    icon={<Plus size={16} />}
                    variant="primary"
                    title={isLand ? 'Add Land' : 'Add Property'}
                    type="button"
                    onClick={() => {
                        navigate(routes.addBrokerProperty, {
                            state: {
                                isPublicProperty: false,
                                item: { user_id: userId, user: userDetail },
                                fromUserDetail: true,
                                isLand,
                            },
                        });
                    }}
                    style={{ whiteSpace: 'nowrap' }}
                />
            </div>

            {/* Table */}
            <div className="overflow-hidden border rounded-3">
                <CustomTable
                    data={dataList}
                    columns={columns}
                    keyField="id"
                    actionList={actionList}
                    emptyMessage={`No ${isLand ? 'lands' : 'properties'} found`}
                    loading={loading}
                    rowClick={(row) => {
                        navigate(`/broker-crm/properties/property-details/${row.id}`, {
                            state: { id: row.id, currentPage: 1 },
                        });
                    }}
                    tbody={{ trClass: 'cursor-pointer' }}
                    pagination={{
                        currentPage: activePage,
                        totalPages,
                        total,
                        onPageChange: (page) => setCurrentPage(page),
                        pageSize,
                        setPageSize: (size) => { setPageSize(size); setCurrentPage(1); },
                        setCurrentPage,
                    }}
                />
            </div>

            {renderPreviewModal()}

            <DeletePopup
                show={showDeletePopup}
                onHide={() => {
                    setShowDeletePopup(false);
                    setItemToDelete(null);
                }}
                onConfirm={handleDeleteConfirm}
                isLoading={deleting}
                saveTitle="Delete"
            />
        </div>
    );
};

export default Properties;
